import React, { useState, useEffect, useContext, useRef } from "react";
import { Line, Pie, Bar } from "react-chartjs-2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { fetchOrders, getRestaurantProfile } from "../utils/api";
import { motion } from "framer-motion";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import AuthContext from "../contexts/AuthContext";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default function Dashboard() {
  const {
    restaurantId,
    loading: authLoading,
    error: authError,
  } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [restaurantName, setRestaurantName] = useState("");
  const [filterDate, setFilterDate] = useState([
    new Date(new Date().getFullYear(), 0, 1),
    new Date(),
  ]);
  const [isRealTime, setIsRealTime] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const refreshIntervalRef = useRef(null);

  const filterStart = filterDate[0];
  const filterEnd = filterDate[1];

  // Check if current filter is same-day
  const isSameDayFilter =
    filterStart &&
    filterEnd &&
    filterStart.toDateString() === filterEnd.toDateString() &&
    filterStart.toDateString() === new Date().toDateString();

  // Fetch restaurant profile
  useEffect(() => {
    if (!restaurantId || authLoading) return;

    console.log("[Dashboard] Fetching restaurant profile for:", restaurantId);
    getRestaurantProfile(restaurantId)
      .then((profile) => {
        console.log("[Dashboard] Got restaurant profile:", profile);
        setRestaurantName(profile.name || "My Restaurant");
      })
      .catch((err) => {
        console.error("[Dashboard] Failed to load restaurant profile:", err);
        setRestaurantName("My Restaurant"); // Fallback name
      });
  }, [restaurantId, authLoading]);

  // Fetch orders function
  const fetchOrdersData = async () => {
    if (!restaurantId || authLoading) return;

    setLoading(true);
    setError(null);
    console.log("[Dashboard] Fetching orders with dates:", {
      start: filterStart,
      end: filterEnd,
      isSameDay: isSameDayFilter,
    });

    const isValidDate = (d) => d instanceof Date && !isNaN(d);
    if (isValidDate(filterStart) && isValidDate(filterEnd)) {
      try {
        const ordersData = await fetchOrders(
          restaurantId,
          filterStart.toISOString(),
          filterEnd.toISOString()
        );
        console.log("[Dashboard] Orders loaded:", ordersData.length);
        setOrders(ordersData || []);
        setLastRefresh(new Date());
        setLoading(false);
      } catch (err) {
        console.error("[Dashboard] Failed to load orders:", err);
        setError("Failed to load orders");
        setOrders([]);
        setLoading(false);
      }
    } else {
      setOrders([]);
      setLoading(false);
    }
  };

  // Fetch orders
  useEffect(() => {
    fetchOrdersData();
  }, [restaurantId, filterStart, filterEnd, authLoading]);

  // Real-time data fetching for same-day filter
  useEffect(() => {
    if (isSameDayFilter) {
      setIsRealTime(true);
      console.log("[Dashboard] Enabling real-time updates for today");

      // Set up auto-refresh every 30 seconds
      refreshIntervalRef.current = setInterval(() => {
        console.log("[Dashboard] Auto-refreshing data...");
        fetchOrdersData();
      }, 30000); // 30 seconds

      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = null;
        }
      };
    } else {
      setIsRealTime(false);
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    }
  }, [isSameDayFilter, restaurantId]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  // Manual refresh function
  const handleManualRefresh = () => {
    fetchOrdersData();
  };

  // Show authentication loading state
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-gray-600">Authenticating...</div>
      </div>
    );
  }

  // Show auth error if any
  if (authError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-red-600">{authError}</div>
      </div>
    );
  }

  // Show message if no restaurant found
  if (!restaurantId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-gray-600">
          No restaurant found for this user.
        </div>
      </div>
    );
  }

  // Calculate metrics from orders
  const pendingOrders = orders.filter(
    (o) => o.status.toLowerCase() === "pending"
  );
  const completedOrders = orders.filter(
    (o) => o.status.toLowerCase() === "completed"
  );
  const pendingRevenue = pendingOrders.reduce(
    (sum, o) => sum + o.total * 1.05,
    0
  );
  const totalRevenue = orders.reduce((sum, o) => sum + o.total * 1.05, 0);
  const totalOrders = orders.length;
  const completedOrdersCount = completedOrders.length;

  // Summary cards data
  const summary = [
    {
      label: "Total Orders",
      value: totalOrders,
      icon: "📊",
      color: "from-green-400 to-blue-500",
    },
    {
      label: "Total Revenue",
      value: `₹${totalRevenue.toFixed(2)}`,
      icon: "💰",
      color: "from-pink-400 to-orange-300",
    },
    {
      label: "Pending Revenue",
      value: `₹${pendingRevenue.toFixed(2)}`,
      icon: "⏳",
      color: "from-yellow-400 to-amber-300",
    },
  ];

  // Pie charts data
  const pieCharts = [
    {
      label: "Order Completion",
      value: totalOrders
        ? Math.round((completedOrdersCount / totalOrders) * 100)
        : 0,
      color: "#f87171",
      icon: "🧾",
    },
    {
      label: "Pending Orders",
      value: totalOrders
        ? Math.round((pendingOrders.length / totalOrders) * 100)
        : 0,
      color: "#34d399",
      icon: "👥",
    },
    {
      label: "Revenue Target",
      value: totalRevenue
        ? Math.min(100, Math.round((totalRevenue / 20000) * 100))
        : 0,
      color: "#60a5fa",
      icon: "💸",
    },
  ];

  // Process orders by day
  const ordersByDay = Array(7).fill(0);
  orders.forEach((o) => {
    if (o.createdAt && o.createdAt._seconds) {
      const d = new Date(o.createdAt._seconds * 1000);
      ordersByDay[d.getDay()]++;
    }
  });

  // Process orders by month for revenue trend
  const revenueByMonth = Array(12).fill(0);
  orders.forEach((o) => {
    if (o.createdAt && o.createdAt._seconds) {
      const d = new Date(o.createdAt._seconds * 1000);
      revenueByMonth[d.getMonth()] += o.total * 1.05;
    }
  });

  // Chart data configurations
  const chartData = {
    orderTrend: {
      labels: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      datasets: [
        {
          label: "Orders",
          data: ordersByDay,
          borderColor: "#f472b6",
          backgroundColor: "rgba(244,114,182,0.2)",
          tension: 0.4,
          fill: true,
        },
      ],
    },
    revenueTrend: {
      labels: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
      datasets: [
        {
          label: "Revenue",
          data: revenueByMonth,
          borderColor: "#60a5fa",
          backgroundColor: "rgba(96,165,250,0.2)",
          tension: 0.4,
          fill: true,
        },
      ],
    },
    customerMap: {
      labels: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      datasets: [
        {
          label: "Orders",
          data: ordersByDay,
          backgroundColor: "#fbbf24",
        },
      ],
    },
  };

  // Recent orders data
  const recentOrders = [...orders]
    .sort((a, b) => (b.createdAt?._seconds || 0) - (a.createdAt?._seconds || 0))
    .slice(0, 5)
    .map((order) => ({
      id: `#${order.id.slice(-5)}`,
      table: order.tableName || `Table ${order.tableId}`,
      status: order.status.charAt(0).toUpperCase() + order.status.slice(1),
      total: `₹${(order.total * 1.05).toFixed(2)}`,
      time:
        order.createdAt && order.createdAt._seconds
          ? new Date(order.createdAt._seconds * 1000).toLocaleTimeString(
              "en-IN",
              {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
                timeZone: "Asia/Kolkata",
              }
            )
          : "",
    }));

  return (
    <div className="min-h-full bg-gradient-to-br from-blue-50 via-pink-50 to-orange-50 p-3 sm:p-4 md:p-8 rounded-xl sm:rounded-2xl">
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 sm:mb-8 gap-4"
      >
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-rose-500 to-amber-500 mb-1">
            Restaurant Dashboard
          </h1>
          <p className="text-sm sm:text-base text-gray-500">
            Analytics and performance overview
          </p>
          {isRealTime && (
            <div className="flex items-center gap-2 mt-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs sm:text-sm text-green-600 font-medium">
                Live Updates Active
              </span>
              <span className="text-xs text-gray-500">
                Last updated: {lastRefresh.toLocaleTimeString()}
              </span>
            </div>
          )}
        </div>

        <motion.div
          whileHover={{ scale: 1.02 }}
          className="flex flex-col sm:flex-row gap-2 items-start sm:items-center"
        >
          <DatePicker
            selectsRange
            startDate={filterDate[0]}
            endDate={filterDate[1]}
            onChange={(update) => setFilterDate(update)}
            dateFormat="MMM d, yyyy"
            className="border border-gray-200 rounded-lg px-3 sm:px-4 py-2 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-transparent text-sm"
            isClearable={true}
            placeholderText="Select date range"
            maxDate={new Date()}
            calendarClassName="shadow-lg rounded-lg border-0"
            popperClassName="shadow-xl"
            popperPlacement="bottom-end"
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
          />
          <div className="flex gap-2">
            <button
              className="px-2 sm:px-3 py-2 rounded-lg bg-gradient-to-r from-blue-200 to-pink-200 text-xs sm:text-sm text-gray-700 font-semibold shadow hover:from-blue-300 hover:to-pink-300 transition"
              onClick={() =>
                setFilterDate([
                  new Date(new Date().getFullYear(), 0, 1),
                  new Date(),
                ])
              }
              type="button"
            >
              All Time
            </button>
            <button
              className="px-2 sm:px-3 py-2 rounded-lg bg-gradient-to-r from-green-200 to-blue-200 text-xs sm:text-sm text-gray-700 font-semibold shadow hover:from-green-300 hover:to-blue-300 transition"
              onClick={() => setFilterDate([new Date(), new Date()])}
              type="button"
            >
              Today
            </button>
            <button
              onClick={handleManualRefresh}
              disabled={loading}
              className="px-2 sm:px-3 py-2 rounded-lg bg-gradient-to-r from-purple-200 to-pink-200 text-xs sm:text-sm text-gray-700 font-semibold shadow hover:from-purple-300 hover:to-pink-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
              type="button"
            >
              {loading ? "🔄" : "🔄"} Refresh
            </button>
          </div>
        </motion.div>
      </motion.div>

      {loading ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex justify-center items-center py-16 sm:py-20"
        >
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-t-2 border-b-2 border-rose-500"></div>
        </motion.div>
      ) : (
        <>
          {/* Summary Cards */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ staggerChildren: 0.1 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8"
          >
            {summary.map((card, index) => (
              <motion.div
                key={card.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`rounded-xl p-4 sm:p-6 shadow-lg text-white bg-gradient-to-r ${card.color} flex items-center gap-3 sm:gap-4 hover:shadow-xl transition-shadow`}
              >
                <span className="text-2xl sm:text-4xl">{card.icon}</span>
                <div>
                  <div className="text-sm sm:text-lg mb-1 opacity-90">
                    {card.label}
                  </div>
                  <div className="text-lg sm:text-2xl font-bold">
                    {card.value}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Charts Row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {/* Pie Charts Container */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 col-span-1"
            >
              <h3 className="font-semibold text-base sm:text-lg text-gray-800 mb-3 sm:mb-4">
                Quick Stats
              </h3>
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                {pieCharts.map((pie, index) => (
                  <motion.div
                    key={pie.label}
                    whileHover={{ scale: 1.05 }}
                    className="flex flex-col items-center"
                  >
                    <div className="relative w-16 h-16 sm:w-20 sm:h-20">
                      <Pie
                        data={{
                          labels: [pie.label],
                          datasets: [
                            {
                              data: [pie.value, 100 - pie.value],
                              backgroundColor: [pie.color, "#f3f4f6"],
                              borderWidth: 0,
                            },
                          ],
                        }}
                        options={{
                          plugins: { legend: { display: false } },
                          cutout: "70%",
                        }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center flex-col">
                        <span className="text-lg sm:text-2xl">{pie.icon}</span>
                        <span className="text-xs font-semibold">
                          {pie.value}%
                        </span>
                      </div>
                    </div>
                    <div className="text-xs font-medium text-gray-600 mt-1 sm:mt-2 text-center">
                      {pie.label}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Order Trend Chart */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 lg:col-span-2"
            >
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 sm:mb-4 gap-2">
                <h3 className="font-semibold text-base sm:text-lg text-gray-800">
                  Order Trend
                </h3>
                
              </div>
              <Line
                data={chartData.orderTrend}
                options={{
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      backgroundColor: "#1f2937",
                      titleColor: "#f9fafb",
                      bodyColor: "#f9fafb",
                      borderColor: "#4b5563",
                      borderWidth: 1,
                      padding: 10,
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: { color: "#f3f4f6" },
                    },
                    x: {
                      grid: { display: false },
                    },
                  },
                }}
                height={120}
              />
            </motion.div>
          </div>

          {/* Charts Row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {/* Revenue Trend */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6"
            >
              <h3 className="font-semibold text-base sm:text-lg text-gray-800 mb-3 sm:mb-4">
                Revenue Trend
              </h3>
              <Line
                data={chartData.revenueTrend}
                options={{
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      callbacks: {
                        label: (context) => `₹${context.raw.toFixed(2)}`,
                      },
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: (value) => `₹${value}`,
                      },
                      grid: { color: "#f3f4f6" },
                    },
                    x: {
                      grid: { display: false },
                    },
                  },
                }}
                height={150}
              />
            </motion.div>

            {/* Customer Map */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6"
            >
              <h3 className="font-semibold text-base sm:text-lg text-gray-800 mb-3 sm:mb-4">
                Customer Traffic
              </h3>
              <Bar
                data={chartData.customerMap}
                options={{
                  plugins: { legend: { display: false } },
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: { color: "#f3f4f6" },
                    },
                    x: {
                      grid: { display: false },
                    },
                  },
                }}
                height={150}
              />
            </motion.div>
          </div>

          {/* Recent Orders */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl sm:rounded-2xl shadow-xl overflow-hidden"
          >
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100">
              <h3 className="font-semibold text-base sm:text-lg text-gray-800">
                Recent Orders
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order ID
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Table
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Time
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentOrders.length > 0 ? (
                    recentOrders.map((order, index) => (
                      <motion.tr
                        key={order.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900">
                          #{order.id.slice(-5)}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-blue-600 hover:underline cursor-pointer">
                          {order.table}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                          <StatusBadge status={order.status} />
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-semibold text-gray-900">
                          {order.total}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500">
                          {order.time}
                        </td>
                      </motion.tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="5"
                        className="px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm text-gray-500"
                      >
                        No recent orders found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        </>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const statusColors = {
    completed: "bg-green-100 text-green-800",
    pending: "bg-amber-100 text-amber-800",
    cancelled: "bg-rose-100 text-rose-800",
    preparing: "bg-blue-100 text-blue-800",
    served: "bg-purple-100 text-purple-800",
  };

  const colorClass =
    statusColors[status.toLowerCase()] || "bg-gray-100 text-gray-800";

  return (
    <span
      className={`px-2 sm:px-3 py-1 rounded-full text-xs font-semibold ${colorClass}`}
    >
      {status}
    </span>
  );
}
